/**
 * Attendance table component - Displays attendance records with actions
 */

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import type { Dochadzka } from "../types";
import { formatTime } from "../utils";

interface Klient {
  id: number;
  alias: string;
  meno: string | null;
  priezvisko: string | null;
}

interface AttendanceTableProps {
  data: Dochadzka[];
  klienti?: Klient[];
  isLoading: boolean;
  /** If omitted, edit/delete actions are hidden (read-only mode). */
  onEdit?: (session: Dochadzka) => void;
  /** If omitted, edit/delete actions are hidden (read-only mode). */
  onDelete?: (id: number) => void;
}

export function AttendanceTable({
  data,
  klienti,
  isLoading,
  onEdit,
  onDelete,
}: AttendanceTableProps) {
  const { t, locale } = useTranslation();

  const getClientDisplay = (session: Dochadzka) => {
    return (
      session.klientAlias ||
      klienti?.find(k => k.id === session.klientId)?.alias ||
      `${session.klientMeno || ""} ${session.klientPriezvisko || ""}`.trim()
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("attendance:sessionRecords")}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            {t("common:loading")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("attendance:table.date")}</TableHead>
                  <TableHead>{t("attendance:table.time")}</TableHead>
                  <TableHead>{t("attendance:table.client")}</TableHead>
                  <TableHead>{t("attendance:table.duration")}</TableHead>
                  <TableHead>{t("attendance:table.services")}</TableHead>
                  <TableHead className="text-right">
                    {t("attendance:table.price")}
                  </TableHead>
                  <TableHead>{t("attendance:table.notes")}</TableHead>
                  {(onEdit || onDelete) && (
                    <TableHead className="text-right">
                      {t("attendance:table.actions")}
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map(session => (
                  <TableRow key={session.id}>
                    <TableCell className="font-medium">
                      {new Date(session.datum).toLocaleDateString(locale)}
                    </TableCell>
                    <TableCell>{formatTime(session.cas)}</TableCell>
                    <TableCell>{getClientDisplay(session)}</TableCell>
                    <TableCell>{session.dlzkaTreningu}</TableCell>
                    <TableCell className="max-w-xs">
                      {/* Show formatted services if available, otherwise fallback to typTreningu */}
                      {session.formattedServices ||
                        session.typTreningu?.join(", ") ||
                        "-"}
                    </TableCell>
                    <TableCell className="text-right">{session.cena}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-xs">
                      {session.poznamka || "-"}
                    </TableCell>
                    {(onEdit || onDelete) && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {onEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onEdit(session)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {onDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onDelete(session.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {data.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center py-8 text-muted-foreground"
                    >
                      {t("attendance:table.noSessions")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
